import * as React from "react";
import { useEffect } from "react";
import SpreetailHeader from "../layout/SpreetailHeader";

interface MarketWatchRefreshConfirmProps {
  isOpen: boolean;
  onRerunAnalysis: () => void;
  onDismiss: () => void;
}

const MarketWatchRefreshConfirm: React.FC<MarketWatchRefreshConfirmProps> = ({
  isOpen,
  onRerunAnalysis,
  onDismiss,
}) => {
  // Lock body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes refreshConfirmFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .refresh-confirm-bg-gradient {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
        `}
      </style>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="refresh-confirm-title"
        className="refresh-confirm-bg-gradient"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          fontFamily: "inherit",
          animation: "refreshConfirmFadeIn 0.2s ease-out forwards",
        }}
      >
        {/* Decorative blur circles */}
        <div
          style={{
            position: "absolute",
            right: -40,
            top: -40,
            width: 200,
            height: 200,
            filter: "blur(40px)",
            pointerEvents: "none",
            background:
              "radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: -32,
            bottom: -32,
            width: 152,
            height: 152,
            filter: "blur(40px)",
            pointerEvents: "none",
            background:
              "radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)",
          }}
        />

        {/* Header */}
        <SpreetailHeader />

        {/* Centered content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "320px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Info icon — teal circle with "i" */}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                backgroundColor: "rgba(6, 142, 117, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 24,
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                  fill="#068E75"
                />
              </svg>
            </div>

            {/* Title */}
            <h2
              id="refresh-confirm-title"
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#1F2937",
                textAlign: "center",
                margin: 0,
                marginBottom: 8,
              }}
            >
              Data Updated
            </h2>

            {/* Description */}
            <p
              style={{
                fontSize: "13px",
                color: "#6B7280",
                textAlign: "center",
                margin: 0,
                marginBottom: 28,
                lineHeight: 1.5,
              }}
            >
              The refreshed data differs from your last analysis. Would you like
              to re-run the analysis with the updated data?
            </p>

            {/* Buttons */}
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              {/* Primary: Re-run Analysis */}
              <button
                onClick={onRerunAnalysis}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  borderRadius: "6px",
                  border: "none",
                  backgroundColor: "#068E75",
                  color: "#FFFFFF",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  lineHeight: 1.4,
                }}
              >
                Yes, Re-run Analysis
              </button>

              {/* Secondary: No */}
              <button
                onClick={onDismiss}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  borderRadius: "6px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "#FFFFFF",
                  color: "#374151",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer",
                  lineHeight: 1.4,
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarketWatchRefreshConfirm;
